import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
  Link,
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Iconify from '../../../components/iconify';
import { loginUser } from '../store/authActions';
import { loginSchema } from '../schemas/authSchemas';

const BRAND_RED = '#E31E24';
const BRAND_GREEN = '#1E5132';

export default function LoginForm() {
  const dispatch = useDispatch();
  const { isLoading, isLoggedIn } = useSelector((state) => state.authReducer);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const onSubmit = (values) => {
    dispatch(loginUser(values));
  };

  useEffect(() => {
    if (isLoggedIn) {
      window.location.href = '/dashboard';
    }
  }, [dispatch, isLoggedIn, navigate]);

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = useFormik({
    initialValues: { username: '', password: '' },
    validationSchema: loginSchema,
    onSubmit,
  });

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            MOBILE NUMBER
          </Typography>
          <TextField
            fullWidth
            name="username"
            value={values.username}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(errors.username) && touched.username}
            placeholder="Enter your mobile number"
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': { borderColor: BRAND_RED },
              },
            }}
          />
          {errors.username && touched.username && (
            <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
              {errors.username}
            </Typography>
          )}
        </Box>

        <Box>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            PASSWORD
          </Typography>
          <TextField
            fullWidth
            name="password"
            placeholder="Enter your password"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.password}
            error={Boolean(errors.password) && touched.password}
            type={showPassword ? 'text' : 'password'}
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': { borderColor: BRAND_RED },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {errors.password && touched.password && (
            <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
              {errors.password}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Link
            component={RouterLink}
            to="/account-recovery"
            variant="caption"
            underline="hover"
            sx={{ color: BRAND_GREEN, fontWeight: 600 }}
          >
            Forgot password?
          </Link>
        </Box>

        <LoadingButton
          loading={isLoading}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          sx={{
            mt: 1,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: '1rem',
            background: `linear-gradient(90deg, ${BRAND_RED} 0%, #c0181d 100%)`,
            boxShadow: `0 4px 14px rgba(227,30,36,0.35)`,
            '&:hover': {
              background: `linear-gradient(90deg, #c0181d 0%, ${BRAND_RED} 100%)`,
              boxShadow: `0 6px 20px rgba(227,30,36,0.45)`,
            },
          }}
        >
          Sign In
        </LoadingButton>
      </Stack>
    </form>
  );
}
